import type { ImportDefinitionVersionDTO } from '@today/types'
import { getCanonicalSchema, ImportDefinitionStatus, type TenantConfigPlane } from '@today/types'
import { ContractJsonView, DryRunSummary, ImportDesignerStepper, PreviewTable } from '@today/ui-domain'
import { Button } from '@today/ui-primitives'
import { type ReactNode, useState } from 'react'
import type { ImportDefinitionSaveError, SaveRequest } from '@/hooks/useImportDefinitions'
import { type DesignerCallError, useImportDesigner } from '@/hooks/useImportDesigner'
import { FieldsStep, MappingStep, ProcessingStepPanel, type StepProps, TargetStep, Violations } from './DesignerSteps'
import { type DesignerState, DesignerStep, stepOfPath } from './designer-state'

const STEPS: { id: DesignerStep; label: string }[] = [
	{ id: DesignerStep.TARGET, label: 'Target' },
	{ id: DesignerStep.SAMPLE, label: 'Sample' },
	{ id: DesignerStep.FIELDS, label: 'Fields' },
	{ id: DesignerStep.MAPPING, label: 'Mapping' },
	{ id: DesignerStep.PROCESSING, label: 'Processing' },
	{ id: DesignerStep.PREVIEW, label: 'Preview' },
	{ id: DesignerStep.DRY_RUN, label: 'Dry run' },
	{ id: DesignerStep.SAVE, label: 'Save' },
]

const SAVE_MESSAGES: Record<ImportDefinitionSaveError['kind'], string> = {
	invalid: 'The definition breaks some field rules — see the steps marked.',
	conflict: 'This version was changed by someone else. Reload it.',
	'draft-exists': 'This definition already has a draft. Edit that draft instead.',
	failed: 'The definition could not be saved — try again.',
}
const CALL_MESSAGES: Record<DesignerCallError['kind'], string> = {
	invalid: 'The definition or the sample breaks some field rules — see the steps marked.',
	'no-importer': 'No importer is registered for this canonical target.',
	failed: 'The request failed — try again.',
}

export interface ImportDesignerProps {
	tenantId: string
	plane: TenantConfigPlane
	initial: DesignerState
	save: (request: SaveRequest) => Promise<ImportDefinitionVersionDTO>
	activate: (key: string, version: number) => Promise<ImportDefinitionVersionDTO>
	onClose: () => void
}

function SampleStep({ upload, state }: { upload: (file: File) => Promise<void> } & Pick<StepProps, 'state'>) {
	return (
		<div className="flex flex-col gap-2 text-ds-sm text-ds-text">
			<label className="flex flex-col gap-1">
				<span>Sample file</span>
				<input
					type="file"
					onChange={(e) => {
						const file = e.target.files?.[0]
						if (file) void upload(file)
						e.target.value = ''
					}}
				/>
			</label>
			{state.sample && state.inspection && (
				<p role="status">
					{state.sample.filename}: {state.inspection.format.type}, {state.inspection.format.encoding}
					{state.inspection.lineLength ? `, ${state.inspection.lineLength} characters per line` : ''}
				</p>
			)}
		</div>
	)
}

const columnsOf = (state: DesignerState) => state.contract.mapping.fields.map((m) => m.target)

/** The import-definition designer (TO-400 Happy path): eight steps over one contract. */
export function ImportDesigner({ tenantId, plane, initial, save, activate, onClose }: ImportDesignerProps) {
	const d = useImportDesigner(tenantId, plane, initial, { save, activate })
	const [step, setStep] = useState(DesignerStep.TARGET)
	const violations = [
		...(d.callError?.kind === 'invalid' ? d.callError.fields : []),
		...(d.saveError?.kind === 'invalid' ? d.saveError.fields : []),
	].filter((f) => stepOfPath(f.path) === step || !f.path.startsWith('contract.'))
	const stepProps: StepProps = { state: d.state, update: d.update }
	const content: Record<DesignerStep, ReactNode> = {
		[DesignerStep.TARGET]: <TargetStep {...stepProps} />,
		[DesignerStep.SAMPLE]: <SampleStep upload={d.upload} state={d.state} />,
		[DesignerStep.FIELDS]: <FieldsStep {...stepProps} />,
		[DesignerStep.MAPPING]: <MappingStep {...stepProps} />,
		[DesignerStep.PROCESSING]: <ProcessingStepPanel {...stepProps} />,
		[DesignerStep.PREVIEW]: <PreviewPanel d={d} />,
		[DesignerStep.DRY_RUN]: <DryRunPanel d={d} />,
		[DesignerStep.SAVE]: <SavePanel d={d} />,
	}
	return (
		<section aria-label="Import definition designer" className="flex flex-col gap-4">
			<div className="flex items-center gap-2">
				<h3 className="font-ds-heading text-ds-lg">{d.state.key || 'New import definition'}</h3>
				<Button variant="muted" size="sm" onPress={onClose}>
					Back to the list
				</Button>
			</div>
			<ImportDesignerStepper steps={STEPS} current={step} onSelect={(id) => setStep(id as DesignerStep)} />
			<Violations fields={violations} />
			{d.callError && d.callError.kind !== 'invalid' && <p role="alert">{CALL_MESSAGES[d.callError.kind]}</p>}
			{d.callError?.kind === 'invalid' && <p role="alert">{CALL_MESSAGES.invalid}</p>}
			{d.saveError && <p role="alert">{SAVE_MESSAGES[d.saveError.kind]}</p>}
			{content[step]}
		</section>
	)
}

type Designer = ReturnType<typeof useImportDesigner>

function PreviewPanel({ d }: { d: Designer }) {
	return (
		<div className="flex flex-col gap-3">
			<Button variant="primary" size="sm" isDisabled={!d.ready || d.busy} onPress={() => void d.runPreview()}>
				Run preview
			</Button>
			{!d.state.sample && <p className="text-ds-sm text-ds-text">Upload a sample first.</p>}
			{d.preview && <PreviewTable preview={d.preview} columns={columnsOf(d.state)} />}
		</div>
	)
}

function DryRunPanel({ d }: { d: Designer }) {
	return (
		<div className="flex flex-col gap-3">
			<Button variant="primary" size="sm" isDisabled={!d.ready || d.busy} onPress={() => void d.runDryRun()}>
				Run dry run
			</Button>
			{d.dryRun && <DryRunSummary dryRun={d.dryRun} />}
		</div>
	)
}

function SavePanel({ d }: { d: Designer }) {
	const { base } = d.state
	const schema = getCanonicalSchema(d.state.canonicalSchema.name, d.state.canonicalSchema.version)
	return (
		<div className="flex flex-col gap-3 text-ds-sm text-ds-text">
			{base && (
				<p role="status">
					Version {base.version}: {base.status}
				</p>
			)}
			<div className="flex gap-2">
				<Button
					variant="primary"
					size="sm"
					isDisabled={d.missing.length > 0 || d.busy || d.state.key === '' || !schema}
					onPress={() => void d.save()}
				>
					Save version
				</Button>
				{base?.status === ImportDefinitionStatus.DRAFT && (
					<Button variant="success" size="sm" isDisabled={d.busy} onPress={() => void d.activate()}>
						Activate
					</Button>
				)}
			</div>
			<ContractJsonView contract={d.contract} onCopy={(json) => void navigator.clipboard?.writeText(json)} />
		</div>
	)
}
